import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, DialogContent } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TBusinessFormData, TFormType } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import FormProvider from 'src/components/hook-form';
import { IBusiness, useStores } from 'src/models';
import { businessApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import { getBusinessFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import removeNullUndefined from 'src/utils/removeNullUndefined';

import { processImagePaths } from '../utils/processImagePath';

import { Common } from './dialog-section/common/Common';
import { Contents } from './dialog-section/contents/Contents';
import { SubListManager } from './dialog-section/sub-list/SubListManager';

interface IBusinessFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: IBusiness;
}

export const BusinessFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: IBusinessFormDialogProps) => {
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore } = useStores();
    const { formId } = useDialogContext();
    const isCreate = formType === FormType.CREATE;

    const methods = useForm<TBusinessFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      // resolver: yupResolver(businessFormValidationSchema),
      defaultValues: getBusinessFormDefaultValues(selectedData),
    });

    // 등록 / 수정 함수
    const onValid = async (data: TBusinessFormData) => {
      // 이미지 처리
      const thumbnail = processImagePaths(
        [data.thumbPath ? data.thumbPath : ''],
        selectedData?.thumbPath ? [selectedData?.thumbPath] : [],
      );
      const desktopImgPath = processImagePaths(
        data.desktopImgPath || [],
        selectedData?.desktopImgPath || [],
      );
      const mobileImgPath = processImagePaths(
        data.mobileImgPath || [],
        selectedData?.mobileImgPath || [],
      );

      const requestData: TBusinessFormData = {
        ...removeNullUndefined(data),
        langCd: data.langCd,
        pipelineSid: data.pipelineSid,
        title: data.title,
        useYn: data.useYn,
        ordr: data.ordr,
        summary: data.summary,
        descript: data.descript,
        link: data.link,
        // 썸네일
        thumbnail: thumbnail.uploadFiles[0],
        thumbPath: thumbnail.existingPaths[0],
        deleteThumbPathFiles: thumbnail.deletePaths,
        // 데스크탑 이미지
        desktopImageFiles: desktopImgPath.uploadFiles,
        desktopImgPath: desktopImgPath.existingPaths,
        deleteDesktopImageFiles: desktopImgPath.deletePaths,
        // 모바일 이미지
        mobileImageFiles: mobileImgPath.uploadFiles,
        mobileImgPath: mobileImgPath.existingPaths,
        deleteMobileImageFiles: mobileImgPath.deletePaths,
        // 서브 컨텐츠
        businessSubList: data.businessSubList?.map((content, idx) => {
          const ordr = idx + 1;
          // 서브 컨텐츠 이미지 처리
          const subDesktopImgPath = processImagePaths(
            content.desktopImgPath ?? [],
            selectedData?.businessSubList?.[idx]?.desktopImgPath ?? [],
          );

          const subMobileImgPath = processImagePaths(
            data.businessSubList?.[idx]?.mobileImgPath ?? [],
            selectedData?.businessSubList?.[idx]?.mobileImgPath ?? [],
          );

          return {
            ...(!isCreate && { businessSubSid: content.businessSubSid }),
            title: content.title,
            descript: content.descript,
            ordr: ordr,
            // 데스크탑
            desktopImageFiles: subDesktopImgPath.uploadFiles,
            desktopImgPath: subDesktopImgPath.existingPaths,
            deleteDesktopImageFiles: subDesktopImgPath.deletePaths,
            // 모바일
            mobileImageFiles: subMobileImgPath.uploadFiles,
            mobileImgPath: subMobileImgPath.existingPaths,
            deleteMobileImageFiles: subMobileImgPath.deletePaths,
          };
        }),
      };

      // API 호출
      const result = isCreate
        ? await businessApi.post(requestData)
        : await businessApi.put(selectedData?.businessSid ?? 0, requestData);

      const responseText = isCreate ? '등록' : '수정';
      if (await result) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      } else {
        console.error(`${responseText} 실패`);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        businessApi
          .remove(selectedData?.businessSid ?? 0)
          .then(() =>
            handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
          );
      });
      alertStore.setProps({
        open: true,
        title: '사업 삭제',
        content: '해당 사업을 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          {/* 기본 정보 + 메인 화면 business의 썸네일 */}
          <Common formType={formType} />
          {/* 컨텐츠: business 카테고리에 노출되는 상세 컨텐츠 */}
          <Contents />
          {/* 서브 컨텐츠: 컨텐츠 외 더 추가하고 싶을 경우 사용 */}
          <SubListManager formType={formType} />
        </DialogContent>
        <DialogActions>
          <>
            {formType === FormType.UPDATE && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {formType === FormType.CREATE ? t('common.button.register') : t('common.button.edit')}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
